import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as ai from "./api";

// --- ticket AI ---

export function useClassify(ticketId: string) {
  return useMutation({ mutationFn: () => ai.classifyTicket(ticketId) });
}

export function useDuplicates(ticketId: string, enabled: boolean) {
  return useQuery({
    queryKey: ["ai-duplicates", ticketId],
    queryFn: () => ai.ticketDuplicates(ticketId),
    enabled,
  });
}

export function useKbSuggestions(ticketId: string, enabled: boolean) {
  return useQuery({
    queryKey: ["ai-kb", ticketId],
    queryFn: () => ai.ticketKbSuggestions(ticketId),
    enabled,
  });
}

export function useSuggestEngineer(ticketId: string, enabled: boolean) {
  return useQuery({
    queryKey: ["ai-engineer", ticketId],
    queryFn: () => ai.suggestEngineer(ticketId),
    enabled,
  });
}

export function useAcceptSuggestion(ticketId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ai.acceptSuggestion,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["ticket", ticketId] });
      void qc.invalidateQueries({ queryKey: ["tickets"] });
    },
  });
}

export function useDismissSuggestion() {
  return useMutation({ mutationFn: ai.dismissSuggestion });
}

export function useSlaRisk(enabled = true) {
  return useQuery({ queryKey: ["sla-risk"], queryFn: ai.slaRisk, enabled });
}

// --- knowledge base ---

export function useKbArticles(params: { search?: string; page?: number }) {
  return useQuery({
    queryKey: ["kb", params],
    queryFn: () => ai.listKb(params),
    placeholderData: (p) => p,
  });
}

function useInvalidateKb() {
  const qc = useQueryClient();
  return () => void qc.invalidateQueries({ queryKey: ["kb"] });
}

export function useCreateKb() {
  const inv = useInvalidateKb();
  return useMutation({ mutationFn: ai.createKb, onSuccess: inv });
}
export function useUpdateKb() {
  const inv = useInvalidateKb();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: Partial<ai.KbArticleBody> }) =>
      ai.updateKb(id, body),
    onSuccess: inv,
  });
}
export function useDeleteKb() {
  const inv = useInvalidateKb();
  return useMutation({ mutationFn: ai.deleteKb, onSuccess: inv });
}

// --- skills ---

export function useSkills(userId: string | null) {
  return useQuery({
    queryKey: ["skills", userId],
    queryFn: () => ai.listSkills(userId as string),
    enabled: userId !== null,
  });
}

export function useSetSkill(userId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ skill, level }: { skill: string; level: number }) =>
      ai.setSkill(userId, skill, level),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["skills", userId] }),
  });
}
export function useDeleteSkill(userId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ai.deleteSkill,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["skills", userId] }),
  });
}
